import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { UseDocumentNumberProps, UseDocumentNumberReturn } from '../types';
import { generateSoChungTuHienTai } from '@/lib/stringUtil';
import { useQuyenChungTuByChungTu } from '@/hooks';
import { QuyenChungTu } from '@/types/schemas';
import { getCurrentNumber } from '../util';

const getQuyenChungTuKey = (quyenChungTu: QuyenChungTu | null) => quyenChungTu?.uuid || quyenChungTu?.ma_nk || '';

/**
 * Custom hook for managing document number generation and QuyenChungTu selection (Simplified)
 *
 * This hook encapsulates the logic for:
 * - Watching date field changes
 * - Fetching QuyenChungTu data based on document type and date
 * - Automatically generating document numbers when QuyenChungTu changes
 * - Simplified to prevent infinite re-renders
 *
 * @param props - Configuration options for the hook
 * @returns Object containing state and handlers for document number management
 */
export const useDocumentNumber = ({
  ma_ct,
  dateFieldName = 'ngay_ct',
  initialQuyenChungTu = null,
  initialSoChungTu = '',
  formMode = 'add'
}: UseDocumentNumberProps): UseDocumentNumberReturn => {
  const { watch } = useFormContext();
  const [quyenChungTu, setQuyenChungTu] = useState<QuyenChungTu | null>(initialQuyenChungTu);
  const [soChungTu, setSoChungTu] = useState<string>(initialSoChungTu);

  // Use refs to track previous values and prevent infinite loops
  const prevInitialSoChungTu = useRef<string>(initialSoChungTu);
  const prevInitialQuyenChungTu = useRef<QuyenChungTu | null>(initialQuyenChungTu);

  // Watch for date field changes
  const ngayCt = watch(dateFieldName);

  // Fetch QuyenChungTu data based on document type and date
  const { quyenChungTus, isLoading, refreshQuyenChungTus } = useQuyenChungTuByChungTu({
    ma_ct,
    ngay_hl: ngayCt || new Date().toISOString().split('T')[0]
  });

  // Sync with external soChungTu changes (only when actually different)
  useEffect(() => {
    if (initialSoChungTu !== prevInitialSoChungTu.current) {
      prevInitialSoChungTu.current = initialSoChungTu;
      setSoChungTu(prev => (prev === initialSoChungTu ? prev : initialSoChungTu));
    }
  }, [initialSoChungTu]);

  // Sync with external quyenChungTu changes (only when actually different)
  useEffect(() => {
    if (getQuyenChungTuKey(initialQuyenChungTu) !== getQuyenChungTuKey(prevInitialQuyenChungTu.current)) {
      prevInitialQuyenChungTu.current = initialQuyenChungTu;
      setQuyenChungTu(prev =>
        getQuyenChungTuKey(prev) === getQuyenChungTuKey(initialQuyenChungTu) ? prev : initialQuyenChungTu
      );
    }
  }, [initialQuyenChungTu]);

  // Auto-generate document number when QuyenChungTu changes
  const generateDocumentNumber = useCallback(
    (selectedQuyenChungTu: QuyenChungTu) => {
      try {
        const generatedSoChungTu = generateSoChungTuHienTai(
          getCurrentNumber(selectedQuyenChungTu, ngayCt),
          selectedQuyenChungTu.so_ct_mau,
          ngayCt
        );
        if (generatedSoChungTu) {
          setSoChungTu(prev => (prev === generatedSoChungTu ? prev : generatedSoChungTu));
          return generatedSoChungTu;
        }
      } catch (error) {
        const fallbackNumber = `${selectedQuyenChungTu.ma_nk || 'DOC'}-${new Date().getTime()}`;
        setSoChungTu(prev => (prev === fallbackNumber ? prev : fallbackNumber));
        return fallbackNumber;
      }
      return '';
    },
    [ngayCt]
  );

  // Memoized setQuyenChungTu that also generates document number
  const handleSetQuyenChungTu = useCallback(
    (newQuyenChungTu: QuyenChungTu | null) => {
      setQuyenChungTu(prev =>
        getQuyenChungTuKey(prev) === getQuyenChungTuKey(newQuyenChungTu) ? prev : newQuyenChungTu
      );
      if (newQuyenChungTu) {
        generateDocumentNumber(newQuyenChungTu);
      }
    },
    [generateDocumentNumber]
  );

  // Regenerate document number when date changes while keeping current QuyenChungTu (only in 'add' mode)
  useEffect(() => {
    if (formMode === 'add' && quyenChungTu && ngayCt) {
      generateDocumentNumber(quyenChungTu);
    }
  }, [formMode, ngayCt, quyenChungTu, generateDocumentNumber]);

  return {
    quyenChungTu,
    setQuyenChungTu: handleSetQuyenChungTu,
    soChungTu,
    setSoChungTu,
    quyenChungTus,
    isLoading,
    refreshQuyenChungTus
  };
};
